import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { Dropdown } from "bootstrap"

/**
 * Filter Popover Controller
 *
 * Powers the popover-based filter UI used across admin index pages.
 *
 * Modes:
 *   - "instant"  → no Apply/Clear footer; clicking a link inside the popover
 *                  navigates immediately (Bootstrap auto-closes the dropdown).
 *   - "explicit" → Apply/Clear footer; navigation happens on Apply, Clear
 *                  resets all inputs and removes this filter's params from
 *                  the URL.
 *
 * Targets:
 *   - panel:           the popover content container
 *   - input:           any form input (checkbox, number, text) whose state
 *                      should be encoded into the URL on Apply
 *   - parentCheckbox:  group "select all" checkboxes that toggle their
 *                      children but never appear in the URL themselves
 *   - applyBtn / clearBtn / searchInput / optionList / option: optional
 *                      targets used by explicit mode and search-select
 *
 * Per-input data attributes:
 *   - data-mode="include"|"exclude": when "exclude", an UNCHECKED box is
 *                      what gets sent to the URL (used for the Programs
 *                      filter on Applications/Applicants where blank means
 *                      "show everything")
 *   - data-empty-marker="true": include-mode groups omit the param when
 *                      nothing is checked. Opt in so an empty group still
 *                      appears in the URL as name[] (Sessions Status / Format
 *                      treat a missing param as the default selection)
 *   - data-group="...": ties a child checkbox to a parentCheckbox so that
 *                      the parent reflects "all checked" state
 */
export default class extends Controller {
  static targets = [
    "panel",
    "input",
    "applyBtn",
    "clearBtn",
    "selectAllBtn",
    "parentCheckbox",
    "searchInput",
    "optionList",
    "option",
    "countBadge"
  ]

  static values = {
    mode: { type: String, default: "instant" },
    applyOnClose: { type: Boolean, default: false },
    navigate: { type: Boolean, default: true },
    basePath: String,
    turboFrame: String
  }

  connect() {
    this._onClick = this.preventClose.bind(this)
    this._onShown = this.captureInitialState.bind(this)
    this._onHidden = this.applyOnHide.bind(this)

    this.element.addEventListener("click", this._onClick)
    this.element.addEventListener("shown.bs.dropdown", this._onShown)
    this.element.addEventListener("hidden.bs.dropdown", this._onHidden)

    this.parentCheckboxTargets.forEach(parent => {
      parent.addEventListener("change", this.handleParentChange.bind(this))
    })
    this.inputTargets.forEach(input => {
      if (input.type === "checkbox" && input.dataset.group) {
        input.addEventListener("change", this.handleChildChange.bind(this))
      }
    })
    this.updateSelectAllLabel()
  }

  disconnect() {
    this.element.removeEventListener("click", this._onClick)
    this.element.removeEventListener("shown.bs.dropdown", this._onShown)
    this.element.removeEventListener("hidden.bs.dropdown", this._onHidden)
  }

  // Footerless filters defer navigation until the popover closes, applying the
  // accumulated selection only when it actually changed.
  applyOnHide() {
    if (!this.applyOnCloseValue) return
    if (!this.selectionChanged()) return

    this.navigate(false)
    this.syncTrigger()
  }

  selectionChanged() {
    if (!this.initialState) return false

    return this.inputTargets.some((input, index) => {
      const initial = this.initialState[index]
      return !initial || input.checked !== initial.checked || input.value !== initial.value
    })
  }

  preventClose(event) {
    const panel = event.target.closest(".filter-popover-panel")
    if (!panel) return

    // Allow links (instant single-select items) and footer buttons to close
    // the dropdown naturally.
    if (event.target.closest("a")) return
    if (event.target.closest(".filter-popover-footer")) return
    if (event.target.closest(".filter-popover-close")) return

    event.stopPropagation()
  }

  captureInitialState() {
    this.initialState = this.inputTargets.map(input => ({
      checked: input.checked,
      value: input.value
    }))
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      this.filterOptions()
      this.searchInputTarget.focus()
    }
    this.updateSelectAllLabel()
  }

  apply() {
    this.navigate(false)
    this.syncTrigger()
    this.hideDropdown()
  }

  clear() {
    this.clearInputs()
    this.navigate(true)
    this.syncTrigger()
    this.hideDropdown()
  }

  // Drops the selection without announcing it, for a popover reused across
  // contexts (a shared modal) that has to start clean each time it is opened.
  reset() {
    this.clearInputs()
    this.syncTrigger()
  }

  clearInputs() {
    this.inputTargets.forEach(input => {
      if (input.type === "checkbox") {
        input.checked = false
      } else {
        input.value = ""
      }
    })
    this.parentCheckboxTargets.forEach(p => { p.checked = false })
    this.updateSelectAllLabel()
  }

  // The trigger's active styling and count normally arrive with the server
  // re-render that follows a navigation. Popovers that only report their
  // selection (`navigate: false`) never get one, so keep the trigger in step.
  syncTrigger() {
    const checkboxes = this.inputTargets.filter(input => input.type === "checkbox")
    if (checkboxes.length === 0) return

    const trigger = this.element.querySelector(".filter-popover-trigger")
    if (!trigger) return

    const count = this.selectedInputValues().length
    trigger.classList.toggle("purple-soft-btn", count > 0)
    trigger.classList.toggle("purple-outline-btn", count === 0)

    if (!this.hasCountBadgeTarget) return

    this.countBadgeTarget.textContent = count
    this.countBadgeTarget.hidden = count === 0
  }

  // Toggle every checkbox in the popover between fully-checked and
  // fully-unchecked. The label flips to "Select all" / "Select none" to
  // reflect the action that the next click will perform.
  toggleSelectAll() {
    const checkboxes = this.inputTargets.filter(i => i.type === "checkbox")
    if (checkboxes.length === 0) return

    const checkAll = !checkboxes.every(c => c.checked)
    checkboxes.forEach(c => { c.checked = checkAll })
    this.parentCheckboxTargets.forEach(p => { p.checked = checkAll })
    this.updateSelectAllLabel()
  }

  updateSelectAllLabel() {
    if (!this.hasSelectAllBtnTarget) return
    const checkboxes = this.inputTargets.filter(i => i.type === "checkbox")
    const allChecked = checkboxes.length > 0 && checkboxes.every(c => c.checked)
    this.selectAllBtnTarget.textContent = allChecked ? "Select none" : "Select all"
  }

  close() {
    this.hideDropdown()
  }

  hideDropdown() {
    const trigger = this.element.querySelector("[data-bs-toggle='dropdown']")
    if (!trigger) return
    Dropdown.getOrCreateInstance(trigger).hide()
  }

  navigate(stripOnly = false) {
    // Reporting popovers own no URL, so clearing is just an empty selection —
    // it still has to be announced or the consumer keeps the stale filter.
    if (!this.navigateValue) {
      this.dispatch("apply", { detail: { values: this.selectedInputValues() } })
      return
    }

    const url = new URL(this.basePathValue, window.location.origin)

    // Strip every param this popover owns so we can rewrite them cleanly.
    const ownedNames = new Set()
    this.inputTargets.forEach(input => {
      const name = input.getAttribute("name")
      if (name) ownedNames.add(name)
    })
    ownedNames.forEach(name => url.searchParams.delete(name))

    if (!stripOnly) {
      this.inputTargets.forEach(input => {
        const name = input.getAttribute("name")
        if (!name) return

        const mode = input.dataset.mode || "include"

        if (input.type === "checkbox") {
          const shouldSend = mode === "exclude" ? !input.checked : input.checked
          if (shouldSend) url.searchParams.append(name, input.value)
        } else {
          const value = (input.value || "").trim()
          if (value) url.searchParams.set(name, value)
        }
      })
      this.appendExcludeModeMarkers(url, ownedNames)
      this.appendIncludeModeEmptyMarkers(url, ownedNames)
    }

    const visitOptions = { action: "advance" }
    if (this.hasTurboFrameValue && this.turboFrameValue) {
      visitOptions.frame = this.turboFrameValue
    }

    Turbo.visit(url.toString(), visitOptions)
  }

  handleParentChange(event) {
    const parent = event.target
    const groupName = parent.dataset.group
    const checked = parent.checked

    this.inputTargets.forEach(input => {
      if (input.dataset.group === groupName) {
        input.checked = checked
      }
    })
    this.updateSelectAllLabel()
  }

  handleChildChange(event) {
    const child = event.target
    const groupName = child.dataset.group
    const parent = this.parentCheckboxTargets.find(p => p.dataset.group === groupName)
    if (!parent) return

    const children = this.inputTargets.filter(i => i.dataset.group === groupName)
    parent.checked = children.length > 0 && children.every(c => c.checked)
    this.updateSelectAllLabel()
  }

  // --- search-select support ----------------------------------------------

  filterOptions() {
    if (!this.hasSearchInputTarget || !this.hasOptionTarget) return
    const term = (this.searchInputTarget.value || "").trim().toLowerCase()

    this.optionTargets.forEach(option => {
      const label = option.dataset.label || option.textContent.toLowerCase()
      option.hidden = term.length > 0 && !label.includes(term)
    })
  }

  // Exclude-mode filters encode UNCHECKED boxes. When every box is checked
  // the URL would have no param, which the server cannot tell from "filter
  // not sent." An empty value marks an explicit "hide nothing" choice.
  appendExcludeModeMarkers(url, ownedNames) {
    ownedNames.forEach(name => {
      const inputs = this.inputTargets.filter(input => input.getAttribute("name") === name)
      if (inputs.length === 0) return

      const exclude = inputs.every(input => (input.dataset.mode || "include") === "exclude")
      if (exclude && !url.searchParams.has(name)) {
        url.searchParams.append(name, "")
      }
    })
  }

  // The inverse of exclude-mode markers, opt-in per input via data-empty-marker.
  // Include-mode checkboxes only append checked values, so an empty group would
  // drop the param and look like "filter never sent."
  appendIncludeModeEmptyMarkers(url, ownedNames) {
    ownedNames.forEach(name => {
      const inputs = this.inputTargets.filter(input => input.getAttribute("name") === name)
      if (inputs.length === 0) return
      if (!inputs.every(input => input.type === "checkbox")) return
      if (!inputs.some(input => input.dataset.emptyMarker === "true")) return

      const include = inputs.every(input => (input.dataset.mode || "include") === "include")
      if (include && !url.searchParams.has(name)) {
        url.searchParams.append(name, "")
      }
    })
  }

  selectedInputValues() {
    return this.inputTargets
      .filter((input) => {
        if (input.type !== "checkbox") return false

        const mode = input.dataset.mode || "include"
        return mode === "exclude" ? !input.checked : input.checked
      })
      .map((input) => input.value)
  }
}
